import { useEffect, useMemo, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { IconMap, loadIconSet } from 'helpers/iconRegistry';
import { isIconSetPath } from 'helpers/iconSet';
import { useConfiguredIconSet } from 'hooks/useIconSet';
import { IconSetContext } from 'components/icons/IconSetContext';

/** Loads the collection's icon set once, rather than per icon. */
export const IconSetProvider = ({ children }: { children: ReactNode }) => {
  const set = useConfiguredIconSet();
  const { pathname } = useLocation();
  const [icons, setIcons] = useState<IconMap | null>(null);

  // Keyed on the set rather than the path, so the chunk stays warm while the visitor is
  // off-gallery and the icons are back the moment they return.
  useEffect(() => {
    if (!set) return undefined;

    let active = true;

    setIcons(null);
    loadIconSet(set).then((loaded) => {
      if (active) setIcons(loaded);
    });

    return () => {
      active = false;
    };
  }, [set]);

  const value = useMemo(
    () => (set && isIconSetPath(pathname) ? { set, icons } : null),
    [set, icons, pathname]
  );

  return <IconSetContext.Provider value={value}>{children}</IconSetContext.Provider>;
};
